import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  Calendar as CalendarIcon,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Compass,
  Lightbulb,
  Sparkles,
  Target,
  Clock3,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  calendarApi,
  calendarConflictsApi,
  dailyFocusBlocksApi,
  driversApi,
  notificationsApi,
  rhythmApi,
  workItemsApi,
} from '../api/services';
import type {
  AnnualStrategicDriver,
  CalendarConflict,
  CalendarEvent,
  CapacitySummary,
  DailyFocusBlock,
  WorkItem,
} from '../types/models';
import { useAuth } from '../auth/AuthProvider';
import { colourForDriverId } from '../theme/tokens';

// ---------------------------------------------------------------------------
// New Tailwind-based dashboard. Replaces the 1443-line MUI DashboardPage —
// the legacy version is preserved at DashboardPageLegacy.tsx for reference.
//
// Layout: SlimRail (left, owned by AppLayout) ⨯ main grid:
//   - Top header: greeting, date, AI Insights pill
//   - Hero: 5-day Daily Activity Timeline (calendar + focus blocks rendered
//     at minute precision, mirrors FocusTimePage's grid behaviour)
//   - Bottom-left: Resource Planning (capacity gauge with optimal range)
//   - Bottom-mid: My Work — Active items, coloured by strategic driver
//   - Right rail (xl+): Strategic Pulse (per-driver progress) + Day Tuning
//     feed (coaching insights + calendar conflicts consolidated, with
//     Resolve / Ignore actions)
// ---------------------------------------------------------------------------

const TIMELINE_DAY_START_HOUR = 7;
const TIMELINE_DAY_END_HOUR = 19;
const TIMELINE_TOTAL_MINUTES = (TIMELINE_DAY_END_HOUR - TIMELINE_DAY_START_HOUR) * 60;
const TIMELINE_PX_PER_HOUR = 36;
const TIMELINE_GRID_HEIGHT =
  (TIMELINE_DAY_END_HOUR - TIMELINE_DAY_START_HOUR) * TIMELINE_PX_PER_HOUR;

function formatDateOnly(d: Date): string {
  // Local-date components — same fix as FocusTimePage avoids the BST
  // off-by-one we shipped against last week.
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function minutesFromDayStart(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h - TIMELINE_DAY_START_HOUR) * 60 + (m || 0);
}

function minutesFromDayStartDate(d: Date): number {
  return (d.getHours() - TIMELINE_DAY_START_HOUR) * 60 + d.getMinutes();
}

function formatHour(hour: number): string {
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const display = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${display}${ampm}`;
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

function shortDate(d: Date): string {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

interface DayTuningEntry {
  id: string;
  kind: 'coaching' | 'conflict-hard' | 'conflict-soft';
  title: string;
  detail: string;
  action?: { label: string; path: string };
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const today = useMemo(() => new Date(), []);
  const [weekStart, setWeekStart] = useState<Date>(() => getMonday(new Date()));
  const weekDates = useMemo(
    () => Array.from({ length: 5 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);

  const [loading, setLoading] = useState(true);
  const [capacity, setCapacity] = useState<CapacitySummary | null>(null);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [focusBlocks, setFocusBlocks] = useState<DailyFocusBlock[]>([]);
  const [drivers, setDrivers] = useState<AnnualStrategicDriver[]>([]);
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [coachingInsights, setCoachingInsights] = useState<
    Array<{ title: string; message: string; entityType?: string; entityId?: string }>
  >([]);
  const [conflicts, setConflicts] = useState<CalendarConflict[]>([]);
  const [dismissedTuning, setDismissedTuning] = useState<Set<string>>(() => new Set());

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [capacityRes, evts, blocks, drvs, items, coach, confs] =
        await Promise.allSettled([
          rhythmApi.getCapacity(formatDateOnly(today)),
          calendarApi.getByRange(formatDateOnly(weekStart), formatDateOnly(weekEnd)),
          dailyFocusBlocksApi.getByRange(formatDateOnly(weekStart), formatDateOnly(weekEnd)),
          driversApi.getActive(),
          workItemsApi.getAll(),
          notificationsApi.getCoachingInsights(),
          calendarConflictsApi.getByDate(formatDateOnly(today)),
        ]);
      if (capacityRes.status === 'fulfilled') setCapacity(capacityRes.value);
      if (evts.status === 'fulfilled') setCalendarEvents(evts.value.filter(e => !e.isAllDay));
      if (blocks.status === 'fulfilled') setFocusBlocks(blocks.value);
      if (drvs.status === 'fulfilled') setDrivers(drvs.value);
      if (items.status === 'fulfilled') setWorkItems(items.value);
      if (coach.status === 'fulfilled') setCoachingInsights(coach.value);
      if (confs.status === 'fulfilled') setConflicts(confs.value);
    } finally {
      setLoading(false);
    }
  }, [today, weekStart, weekEnd]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const tuningEntries: DayTuningEntry[] = useMemo(() => {
    const entries: DayTuningEntry[] = [];
    coachingInsights.forEach((c, idx) => {
      entries.push({
        id: `coach-${idx}`,
        kind: 'coaching',
        title: c.title,
        detail: c.message,
        action: c.entityType
          ? {
              label: 'Open',
              path:
                c.entityType === 'WorkItem'
                  ? `/work-items?id=${c.entityId}`
                  : c.entityType === 'StrategicDriver'
                    ? `/strategy?id=${c.entityId}`
                    : c.entityType === 'Project'
                      ? `/projects/${c.entityId}`
                      : '/dashboard',
            }
          : undefined,
      });
    });
    conflicts.forEach((c, idx) => {
      entries.push({
        id: `conflict-${idx}`,
        kind: c.severity === 'Hard' ? 'conflict-hard' : 'conflict-soft',
        title: c.severity === 'Hard' ? 'Calendar conflict' : 'Soft overlap',
        detail: c.description,
        action: { label: 'Resolve', path: '/calendar' },
      });
    });
    return entries.filter(e => !dismissedTuning.has(e.id));
  }, [coachingInsights, conflicts, dismissedTuning]);

  const dismissTuning = (id: string) => {
    setDismissedTuning(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const activeItems = useMemo(() => {
    const todayKey = formatDateOnly(today);
    return workItems
      .filter(
        item =>
          (item.targetDate && item.targetDate.startsWith(todayKey)) ||
          item.status === 'InProgress',
      )
      .slice(0, 6);
  }, [today, workItems]);

  const greeting = useMemo(() => {
    const hour = today.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  }, [today]);

  const heroDateLabel = today.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <TooltipProvider delayDuration={200}>
      {/* Calm gradient mesh — soft pastel wash that's the same in light and
          shifted-blue in dark. Sits behind the cards' translucent surfaces.
          The blobs are a touch more saturated than usual so they show
          through the macOS-style glass cards above instead of washing out. */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-violet-50 to-rose-50 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950" />
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-blue-400/30 blur-3xl dark:bg-blue-500/20" />
        <div className="absolute top-1/4 right-0 h-[460px] w-[460px] rounded-full bg-violet-400/25 blur-3xl dark:bg-violet-500/20" />
        <div className="absolute bottom-0 left-1/3 h-[400px] w-[400px] rounded-full bg-amber-300/20 blur-3xl dark:bg-amber-500/15" />
        <div className="absolute bottom-1/4 right-1/4 h-[360px] w-[360px] rounded-full bg-rose-300/20 blur-3xl dark:bg-rose-500/15" />
      </div>

      <div className="flex w-full flex-col gap-4 p-4 lg:p-6">
        {/* Header */}
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-sm text-muted-foreground">{heroDateLabel}</p>
            <h1 className="mt-0.5 text-2xl font-semibold tracking-tight text-foreground">
              {greeting}
              {user?.displayName ? `, ${user.displayName.split(' ')[0]}` : ''}.
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Badge variant="muted" className="gap-1.5 px-2 py-1 text-xs">
                    <Sparkles className="h-3 w-3" />
                    AI Insights on
                  </Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Coaching, conflict detection and time-allocation hints surface inside cards as small lightbulb icons.
              </TooltipContent>
            </Tooltip>
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/plan')}
            >
              Open today's plan
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </header>

        {/* Spatial layout:
              Left column (flexible): Timeline on top, Resource Planning + My Work below
              Right column (fixed 360px on xl, full-width below): Strategic Pulse + Day Tuning
            On lg- screens the right rail wraps below at full width. */}
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="flex flex-col gap-4">
        {/* HERO — 5-day timeline */}
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-3 pb-2">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4 text-primary" />
                Daily Activity Timeline
              </CardTitle>
              <p className="mt-1 text-xs text-muted-foreground">
                {shortDate(weekStart)} – {shortDate(addDays(weekStart, 4))}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="icon-sm"
                variant="ghost"
                onClick={() => setWeekStart(d => addDays(d, -7))}
                aria-label="Previous week"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setWeekStart(getMonday(new Date()))}
                disabled={formatDateOnly(weekStart) === formatDateOnly(getMonday(new Date()))}
              >
                This week
              </Button>
              <Button
                size="icon-sm"
                variant="ghost"
                onClick={() => setWeekStart(d => addDays(d, 7))}
                aria-label="Next week"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-3">
            <WeeklyTimeline
              weekDates={weekDates}
              calendarEvents={calendarEvents}
              focusBlocks={focusBlocks}
              loading={loading}
            />
          </CardContent>
        </Card>

        {/* Bottom of left column: Resource Planning + My Work side-by-side */}
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" />
                Resource Planning
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Today's capacity vs your working window.
              </p>
            </CardHeader>
            <CardContent className="pt-2">
              <CapacityGauge capacity={capacity} loading={loading} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Compass className="h-4 w-4 text-primary" />
                My Work — Today
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                In-progress and due-today, coloured by strategic driver.
              </p>
            </CardHeader>
            <CardContent className="pt-2">
              <ActiveWorkList
                items={activeItems}
                loading={loading}
                onOpen={() => navigate('/work-items')}
                onComplete={async (id) => {
                  // Optimistic — flip the row to Done locally then call the
                  // generic update endpoint. If the API rejects we surface a
                  // simple alert and reload the canonical list. The full
                  // diagnostic / undo flow lives on the WorkItems page.
                  setWorkItems(prev =>
                    prev.map(w => (w.id === id ? { ...w, status: 'Done' } : w)),
                  );
                  try {
                    await workItemsApi.update(id, { status: 'Done' });
                  } catch {
                    void loadAll();
                  }
                }}
              />
            </CardContent>
          </Card>
        </div>
        </div>

          {/* Right rail — Strategic Pulse + Day Tuning */}
          <div className="flex flex-col gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Compass className="h-4 w-4 text-primary" />
                  Strategic Pulse
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Annual drivers — at-a-glance progress.
                </p>
              </CardHeader>
              <CardContent className="pt-2">
                <StrategicPulse
                  drivers={drivers}
                  loading={loading}
                  onOpen={(id) => navigate(`/strategy?id=${id}`)}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-amber-500" />
                  Day Tuning
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Coaching prompts and calendar conflicts in one feed.
                </p>
              </CardHeader>
              <CardContent className="pt-2">
                <DayTuningFeed
                  entries={tuningEntries}
                  loading={loading}
                  onAction={(entry) => entry.action && navigate(entry.action.path)}
                  onDismiss={dismissTuning}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Weekly Timeline — minute-positioned 5-day grid
// ---------------------------------------------------------------------------

interface WeeklyTimelineProps {
  weekDates: Date[];
  calendarEvents: CalendarEvent[];
  focusBlocks: DailyFocusBlock[];
  loading: boolean;
}

function WeeklyTimeline({
  weekDates,
  calendarEvents,
  focusBlocks,
  loading,
}: WeeklyTimelineProps) {
  // Calendar events that already have a focus-block representation. Two
  // sources contribute:
  //   1. Manual blocks created via FocusTimePage's "Mark as focus" action —
  //      these carry an explicit linkedCalendarEventId.
  //   2. DetectedFromCalendar blocks generated by the backend pattern matcher
  //      from events titled "Focus Time" / "Deep Work" etc. The matcher
  //      doesn't currently store the source event id back on the block, so we
  //      match by exact date + start/end time as a frontend dedup until the
  //      server is taught to write LinkedCalendarEventId on detected blocks.
  // Without this, a recurring "Focus Time" calendar entry would render twice:
  // once as a focus block, once as the underlying meeting, producing the
  // stacked labels visible on wide-meeting weeks.
  const linkedEventIds = useMemo(() => {
    const set = new Set<string>();
    focusBlocks.forEach(b => {
      if (b.linkedCalendarEventId) set.add(b.linkedCalendarEventId);
    });
    if (focusBlocks.some(b => b.source === 'DetectedFromCalendar')) {
      calendarEvents.forEach(evt => {
        const start = new Date(evt.startTime);
        const end = new Date(evt.endTime);
        const evDate = formatDateOnly(start);
        const evStart = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
        const evEnd = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;
        const matched = focusBlocks.some(b =>
          b.source === 'DetectedFromCalendar'
          && b.date === evDate
          && b.startTime.startsWith(evStart)
          && b.endTime.startsWith(evEnd),
        );
        if (matched) set.add(evt.id);
      });
    }
    return set;
  }, [calendarEvents, focusBlocks]);

  const eventsByDate = useMemo(() => {
    // Three layers of suppression to stop calendar events double-rendering
    // alongside the focus blocks:
    //   1. linkedEventIds — explicit linked events from Manual blocks +
    //      time-matched Detected blocks.
    //   2. Title-shape match — if the title is focus-shaped AND any focus
    //      block exists at the same slot, the meeting layer is suppressed.
    //      Catches the case where the backend hasn't allocated yet (no
    //      Detected block to match against) but the user already has the
    //      "Focus Time" event in their diary.
    //   3. Signature dedup — same date + start + end + title → keep first.
    //      Catches Outlook recurring + one-off overrides and multi-source
    //      sync duplicates.
    const FOCUS_TITLE_REGEXES = [
      /focus/i,
      /deep\s*work/i,
      /no\s*meetings/i,
      /\bDND\b/,
      /do\s*not\s*disturb/i,
      /\bprotected\b/i,
    ];
    const isFocusShaped = (t: string) => FOCUS_TITLE_REGEXES.some(rx => rx.test(t));
    const seen = new Set<string>();
    const map: Record<
      string,
      Array<{
        id: string;
        startMinute: number;
        endMinute: number;
        title: string;
        isPriority: boolean;
      }>
    > = {};
    weekDates.forEach(d => { map[formatDateOnly(d)] = []; });
    calendarEvents.forEach(evt => {
      if (linkedEventIds.has(evt.id)) return; // drawn as a focus block instead
      const start = new Date(evt.startTime);
      const end = new Date(evt.endTime);
      const key = formatDateOnly(start);
      if (!map[key]) return;
      const sig = `${key}|${start.getHours()}:${start.getMinutes()}|${end.getHours()}:${end.getMinutes()}|${evt.title.trim().toLowerCase()}`;
      if (seen.has(sig)) return;
      seen.add(sig);
      if (isFocusShaped(evt.title)) {
        const evStart = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
        const evEnd = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;
        const blockExistsAtSlot = focusBlocks.some(b =>
          b.date === key && b.startTime.startsWith(evStart) && b.endTime.startsWith(evEnd),
        );
        if (blockExistsAtSlot) return;
      }
      const startMinute = minutesFromDayStartDate(start);
      const endMinute = minutesFromDayStartDate(end);
      if (endMinute <= 0 || startMinute >= TIMELINE_TOTAL_MINUTES) return;
      map[key].push({
        id: evt.id,
        startMinute: Math.max(0, startMinute),
        endMinute: Math.min(TIMELINE_TOTAL_MINUTES, endMinute),
        title: evt.title,
        isPriority: evt.isPriorityMeeting,
      });
    });
    return map;
  }, [calendarEvents, focusBlocks, linkedEventIds, weekDates]);

  const blocksByDate = useMemo(() => {
    const map: Record<string, DailyFocusBlock[]> = {};
    weekDates.forEach(d => { map[formatDateOnly(d)] = []; });
    focusBlocks.forEach(b => { if (map[b.date]) map[b.date].push(b); });
    return map;
  }, [focusBlocks, weekDates]);

  const todayKey = formatDateOnly(new Date());

  return (
    <div className="overflow-x-auto">
      <div
        className="grid min-w-[640px] gap-0"
        style={{ gridTemplateColumns: '52px repeat(5, minmax(0, 1fr))' }}
      >
        <div />
        {weekDates.map((d, i) => {
          const isToday = formatDateOnly(d) === todayKey;
          return (
            <div
              key={i}
              className={cn(
                'border-b border-border/40 py-2 text-center',
                isToday && 'bg-primary/5',
              )}
            >
              <div
                className={cn(
                  'text-xs font-semibold',
                  isToday ? 'text-primary' : 'text-muted-foreground',
                )}
              >
                {WEEKDAY_LABELS[i]}
              </div>
              <div className="text-[10px] text-muted-foreground">{shortDate(d)}</div>
            </div>
          );
        })}

        <div className="relative" style={{ height: TIMELINE_GRID_HEIGHT }}>
          {Array.from(
            { length: TIMELINE_DAY_END_HOUR - TIMELINE_DAY_START_HOUR },
            (_, i) => (
              <div
                key={i}
                className="absolute right-1.5 text-[10px] text-muted-foreground"
                style={{ top: i * TIMELINE_PX_PER_HOUR }}
              >
                {formatHour(TIMELINE_DAY_START_HOUR + i)}
              </div>
            ),
          )}
        </div>

        {weekDates.map((date, di) => {
          const key = formatDateOnly(date);
          const isToday = key === todayKey;
          const dayEvents = eventsByDate[key] || [];
          const dayBlocks = blocksByDate[key] || [];
          return (
            <div
              key={di}
              className={cn(
                'relative border-l border-border/40',
                isToday && 'bg-primary/5',
              )}
              style={{
                height: TIMELINE_GRID_HEIGHT,
                background: `repeating-linear-gradient(to bottom, transparent 0, transparent ${TIMELINE_PX_PER_HOUR - 1}px, hsl(var(--border) / 0.45) ${TIMELINE_PX_PER_HOUR - 1}px, hsl(var(--border) / 0.45) ${TIMELINE_PX_PER_HOUR}px)`,
              }}
            >
              {dayBlocks.map(block => {
                const startMin = minutesFromDayStart(block.startTime);
                const endMin = minutesFromDayStart(block.endTime);
                if (endMin <= 0 || startMin >= TIMELINE_TOTAL_MINUTES) return null;
                const top = (Math.max(0, startMin) / 60) * TIMELINE_PX_PER_HOUR;
                const height =
                  ((Math.min(TIMELINE_TOTAL_MINUTES, endMin) - Math.max(0, startMin)) / 60) *
                  TIMELINE_PX_PER_HOUR;
                return (
                  <div
                    key={block.id}
                    className="absolute left-0.5 right-0.5 z-10 flex items-start gap-1 overflow-hidden rounded-md border border-blue-300/50 bg-blue-100/60 px-1.5 py-0.5 text-[10px] font-medium text-blue-900 dark:border-blue-400/30 dark:bg-blue-500/15 dark:text-blue-100"
                    style={{ top, height }}
                  >
                    <Target className="mt-0.5 h-2.5 w-2.5 shrink-0" />
                    <span className="truncate">{block.label}</span>
                  </div>
                );
              })}

              {dayEvents.map(evt => {
                const top = (evt.startMinute / 60) * TIMELINE_PX_PER_HOUR;
                const height = Math.max(
                  16,
                  ((evt.endMinute - evt.startMinute) / 60) * TIMELINE_PX_PER_HOUR,
                );
                return (
                  <div
                    key={evt.id}
                    className={cn(
                      'absolute left-1.5 right-1.5 z-20 overflow-hidden rounded-md border border-l-2 px-1.5 py-0.5 text-[10px] font-medium',
                      evt.isPriority
                        ? 'border-amber-400/60 border-l-amber-500 bg-amber-100/60 text-amber-900 dark:border-amber-400/40 dark:bg-amber-400/15 dark:text-amber-100'
                        : 'border-rose-300/50 border-l-rose-400 bg-rose-100/40 text-rose-900 dark:border-rose-400/30 dark:bg-rose-500/15 dark:text-rose-100',
                    )}
                    style={{ top, height }}
                  >
                    <span className="truncate">{evt.title}</span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {loading && (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock3 className="h-3 w-3 animate-spin" /> Loading week…
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Capacity Gauge — semi-circle SVG with optimal-range arc
// ---------------------------------------------------------------------------

interface CapacityGaugeProps {
  capacity: CapacitySummary | null;
  loading: boolean;
}

function CapacityGauge({ capacity, loading }: CapacityGaugeProps) {
  // The semicircle path total length is ~282 (πr where r=90). We dash on a
  // proportion of that to draw the indicator + the optimal-range band.
  const ARC_LEN = 282;
  const value = capacity ? Math.min(150, Math.max(0, capacity.utilisationPercent)) : 0;
  const valueAngle = (value / 100) * 180 - 90;
  const tone =
    value < 70 ? 'success' : value < 95 ? 'primary' : value < 110 ? 'warning' : 'danger';
  const toneClasses = {
    success: 'text-emerald-600 dark:text-emerald-400',
    primary: 'text-primary',
    warning: 'text-amber-600 dark:text-amber-400',
    danger: 'text-rose-600 dark:text-rose-400',
  };

  if (!capacity && !loading) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        No capacity data yet — run Start My Day to populate.
      </p>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <div className="relative flex h-[140px] w-full max-w-[260px] items-end justify-center">
        <svg viewBox="0 0 200 110" className="h-full w-full">
          {/* Track */}
          <path
            d="M 10 100 A 90 90 0 0 1 190 100"
            fill="none"
            stroke="hsl(var(--muted))"
            strokeWidth="14"
            strokeLinecap="round"
          />
          {/* Optimal range — 70% to 95% of the arc */}
          <path
            d="M 10 100 A 90 90 0 0 1 190 100"
            fill="none"
            stroke="hsl(142 71% 45% / 0.35)"
            strokeWidth="14"
            strokeLinecap="butt"
            strokeDasharray={`0 ${(ARC_LEN * 70) / 100} ${(ARC_LEN * 25) / 100} ${ARC_LEN}`}
          />
          {/* Indicator */}
          <path
            d="M 10 100 A 90 90 0 0 1 190 100"
            fill="none"
            stroke="currentColor"
            strokeWidth="14"
            strokeLinecap="round"
            className={cn('transition-all duration-standard ease-standard', toneClasses[tone])}
            strokeDasharray={`${(Math.max(0, Math.min(100, value)) / 100) * ARC_LEN} ${ARC_LEN}`}
          />
          {capacity && (
            <line
              x1="100"
              y1="100"
              x2={100 + 70 * Math.cos(((valueAngle - 90) * Math.PI) / 180)}
              y2={100 + 70 * Math.sin(((valueAngle - 90) * Math.PI) / 180)}
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              className={toneClasses[tone]}
            />
          )}
          <circle cx="100" cy="100" r="4" fill="currentColor" className={toneClasses[tone]} />
        </svg>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-center">
          <div className={cn('text-3xl font-semibold leading-none', toneClasses[tone])}>
            {capacity ? Math.round(capacity.utilisationPercent) : 0}%
          </div>
          <div className="mt-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">
            Utilisation
          </div>
        </div>
      </div>

      {capacity && (
        <div className="mt-3 grid w-full grid-cols-3 gap-2">
          <CapacityStat label="Meetings" minutes={capacity.meetingMinutes} tone="warning" />
          <CapacityStat label="Planned" minutes={capacity.plannedMinutes} tone="primary" />
          <CapacityStat label="Free" minutes={capacity.availableMinutes} tone="success" />
        </div>
      )}

      {capacity && (
        <p className="mt-3 text-center text-xs text-muted-foreground">
          Optimal range{' '}
          <span className="text-emerald-600 dark:text-emerald-400">70 – 95%</span>
          {capacity.isOverloaded && (
            <span className="ml-1.5 text-amber-600 dark:text-amber-400">· Overloaded</span>
          )}
        </p>
      )}
    </div>
  );
}

function CapacityStat({
  label,
  minutes,
  tone,
}: {
  label: string;
  minutes: number;
  tone: 'primary' | 'success' | 'warning' | 'danger';
}) {
  const hours = (minutes / 60).toFixed(1);
  const toneClasses = {
    primary: 'text-primary',
    success: 'text-emerald-600 dark:text-emerald-400',
    warning: 'text-amber-700 dark:text-amber-400',
    danger: 'text-rose-600 dark:text-rose-400',
  };
  return (
    <div className="rounded-lg border border-border/50 bg-background/40 p-2 text-center">
      <div className={cn('text-base font-semibold', toneClasses[tone])}>{hours}h</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Active Work list
// ---------------------------------------------------------------------------

function ActiveWorkList({
  items,
  loading,
  onOpen,
  onComplete,
}: {
  items: WorkItem[];
  loading: boolean;
  onOpen: () => void;
  onComplete: (id: string) => void | Promise<void>;
}) {
  if (!loading && items.length === 0) {
    return (
      <div className="py-6 text-center">
        <CheckCircle2 className="mx-auto h-7 w-7 text-emerald-500" />
        <p className="mt-2 text-sm font-medium">Nothing active for today.</p>
        <p className="text-xs text-muted-foreground">
          Plan your day to populate — or add a work item.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <ul className="divide-y divide-border/40">
        {items.map(item => {
          const driverColor = colourForDriverId(item.strategicDriverId);
          const isDone = item.status === 'Done';
          return (
            <li
              key={item.id}
              className={cn(
                'grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 px-1 py-2 text-sm transition-opacity',
                isDone && 'opacity-60',
              )}
            >
              {/* Mark-done checkbox. A circle that fills in on hover for
                  open items; ticked when done. Click toggles to Done — undo
                  lives on the Work Items page. */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={isDone ? 'Done' : 'Mark done'}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isDone) onComplete(item.id);
                    }}
                    disabled={isDone}
                    className={cn(
                      'group grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      isDone
                        ? 'border-emerald-500 bg-emerald-500 text-white cursor-default'
                        : 'border-muted-foreground/40 hover:border-emerald-500 hover:bg-emerald-500/10',
                    )}
                  >
                    <Check
                      className={cn(
                        'h-3 w-3 transition-opacity',
                        isDone ? 'opacity-100' : 'opacity-0 group-hover:opacity-50',
                      )}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{isDone ? 'Marked done' : 'Mark done'}</TooltipContent>
              </Tooltip>

              <div className="flex min-w-0 items-center gap-2">
                <span
                  aria-hidden
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: driverColor }}
                />
                <span
                  className={cn(
                    'truncate font-medium',
                    isDone && 'line-through text-muted-foreground',
                  )}
                >
                  {item.title}
                </span>
              </div>
              <Badge
                variant={
                  item.priority === 'High'
                    ? 'danger'
                    : item.priority === 'Medium'
                      ? 'warning'
                      : 'muted'
                }
                className="justify-self-end"
              >
                {item.priority}
              </Badge>
              <Badge
                variant={
                  item.status === 'Done'
                    ? 'success'
                    : item.status === 'InProgress'
                      ? 'default'
                      : 'outline'
                }
                className="justify-self-end"
              >
                {item.status}
              </Badge>
            </li>
          );
        })}
      </ul>
      <Button
        variant="ghost"
        size="sm"
        onClick={onOpen}
        className="mt-2 self-start px-2"
      >
        View all work items
        <ArrowRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Strategic Pulse — driver progress
// ---------------------------------------------------------------------------

function StrategicPulse({
  drivers,
  loading,
  onOpen,
}: {
  drivers: AnnualStrategicDriver[];
  loading: boolean;
  onOpen: (id: string) => void;
}) {
  if (!loading && drivers.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-muted-foreground">
        No active drivers — head to Strategy to set yours.
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-3">
      {drivers.slice(0, 6).map(driver => {
        const colour = colourForDriverId(driver.id);
        const progress = Math.max(0, Math.min(100, driver.progressPercentage ?? 0));
        return (
          <li key={driver.id}>
            <button
              type="button"
              onClick={() => onOpen(driver.id)}
              className="group block w-full rounded-lg p-1 text-left transition-colors hover:bg-accent/50"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="flex items-center gap-1.5 text-sm font-medium">
                  <span
                    aria-hidden
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: colour }}
                  />
                  <span className="truncate">{driver.title}</span>
                </span>
                <span className="text-xs font-semibold text-foreground">
                  {Math.round(progress)}%
                </span>
              </div>
              <Progress
                value={progress}
                tone="driver"
                indicatorColor={colour}
                className="mt-1"
              />
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Day Tuning feed — coaching + conflicts consolidated
// ---------------------------------------------------------------------------

function DayTuningFeed({
  entries,
  loading,
  onAction,
  onDismiss,
}: {
  entries: DayTuningEntry[];
  loading: boolean;
  onAction: (entry: DayTuningEntry) => void;
  onDismiss: (id: string) => void;
}) {
  if (!loading && entries.length === 0) {
    return (
      <div className="py-4 text-center">
        <CheckCircle2 className="mx-auto h-6 w-6 text-emerald-500" />
        <p className="mt-1.5 text-sm font-medium">No tuning needed.</p>
        <p className="text-xs text-muted-foreground">
          No conflicts and no coaching prompts for today.
        </p>
      </div>
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {entries.map(entry => {
        const palette =
          entry.kind === 'conflict-hard'
            ? 'border-rose-300/40 bg-rose-50/40 dark:border-rose-400/20 dark:bg-rose-500/10'
            : entry.kind === 'conflict-soft'
              ? 'border-amber-300/40 bg-amber-50/50 dark:border-amber-400/20 dark:bg-amber-500/10'
              : 'border-blue-300/40 bg-blue-50/50 dark:border-blue-400/20 dark:bg-blue-500/10';
        const Icon = entry.kind === 'coaching' ? Lightbulb : AlertTriangle;
        const iconColor =
          entry.kind === 'conflict-hard'
            ? 'text-rose-500'
            : entry.kind === 'conflict-soft'
              ? 'text-amber-500'
              : 'text-blue-500';
        return (
          <li
            key={entry.id}
            className={cn(
              'flex flex-col gap-1.5 rounded-xl border p-2.5 text-xs',
              palette,
            )}
          >
            <div className="flex items-start gap-2">
              <Icon className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', iconColor)} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold leading-tight">{entry.title}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{entry.detail}</p>
              </div>
            </div>
            <div className="flex justify-end gap-1.5">
              <Button size="sm" variant="ghost" onClick={() => onDismiss(entry.id)}>
                Ignore
              </Button>
              {entry.action && (
                <Button size="sm" variant="default" onClick={() => onAction(entry)}>
                  {entry.action.label}
                  <ArrowRight className="h-3 w-3" />
                </Button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
